import {
  PortalQuoteRespondRequestSchema,
  PortalQuoteRespondRequestType,
  PortalQuoteRespondResponseSchema,
  PortalQuoteRespondResponseType,
  PortalQuoteResponseSchema,
  PortalQuoteResponseType,
} from 'stringline-types';

const API_BASE_URL: string = (import.meta.env.VITE_API_URL ?? '').trim();

if (!API_BASE_URL) {
  // Surfaced as a console warning rather than a throw so the bundle can still load and render
  // a generic error if a deploy forgets to set the env var.
  // eslint-disable-next-line no-console
  console.warn('VITE_API_URL is not set. Portal API calls will fail.');
}

export class PortalApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'PortalApiError';
    this.status = status;
  }
}

const buildUrl = (path: string): string => {
  const base = API_BASE_URL.replace(/\/+$/, '');
  return `${base}${path}`;
};

const extractErrorMessage = async (response: Response): Promise<string> => {
  try {
    const body: unknown = await response.json();
    if (
      body !== null &&
      typeof body === 'object' &&
      'error' in body &&
      body.error !== null &&
      typeof body.error === 'object' &&
      'message' in body.error &&
      typeof (body.error as { message: unknown }).message === 'string'
    ) {
      return (body.error as { message: string }).message;
    }
  } catch {
    // ignore JSON parse errors
  }
  return `Request failed with status ${response.status}`;
};

export const fetchPortalQuote = async (token: string): Promise<PortalQuoteResponseType> => {
  const response = await fetch(buildUrl(`/v1/portal/quotes/${encodeURIComponent(token)}`), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    const message = await extractErrorMessage(response);
    throw new PortalApiError(response.status, message);
  }

  const json: unknown = await response.json();
  return PortalQuoteResponseSchema.parse(json);
};

export const submitPortalQuoteResponse = async (
  token: string,
  body: PortalQuoteRespondRequestType
): Promise<PortalQuoteRespondResponseType> => {
  const validatedBody = PortalQuoteRespondRequestSchema.parse(body);

  const response = await fetch(buildUrl(`/v1/portal/quotes/${encodeURIComponent(token)}/respond`), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(validatedBody),
  });

  if (!response.ok) {
    const message = await extractErrorMessage(response);
    throw new PortalApiError(response.status, message);
  }

  const json: unknown = await response.json();
  return PortalQuoteRespondResponseSchema.parse(json);
};
